// @flow

import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Link,
  CoverMedia,
  LazyloadPlaceholder,
  MediaQuery,
  SliderPagination,
  scrollToElement,
} from '@riseart/common';
import { CardHorizontal } from '@riseart/card';
import { GridSquare, GridSquareItem, GridPaginationWrapper } from '@riseart/grid';
import { WithLocale } from 'shared_hocs/common/WithLocale';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { Pagination } from 'shared_hocs/pagination/Pagination';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';
import { Avatar } from 'shared_components/common/avatar/Avatar';

import { raScreenSmMax, raScreenSm } from '@riseart/antd-provider/dist/website/variables.less';
import { collectionGridCls } from 'shared_components/collections/containers/grid/Grid.less';

type Props = {
  isLoading: boolean,
  items: Array<Object>,
  pageUrl: string,
  paginationType: string,
  pagination: Object,
};

/**
 * CollectionGrid
 */
export class CollectionGrid extends Component<Props, *> {
  gridRef: Object = null;

  scrollToTopOfGrid: Function;

  /**
   * constructor
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.gridRef = React.createRef();

    this.bindMethods();
  }

  /**
   * bindMethods
   */
  bindMethods() {
    const GRID_SCROLL_TO_TOP_OFFSET = 50;
    this.scrollToTopOfGrid = scrollToElement.bind(this, this.gridRef, GRID_SCROLL_TO_TOP_OFFSET);
  }

  /**
   * render
   */
  render() {
    const LAZYLOAD_HEIGHT = '100%';
    const LAZYLOAD_OFFSET = 200;
    const {
      isLoading = false,
      items = [],
      pageUrl,
      paginationType,
      pagination: paginationProps,
    } = this.props;

    return (
      <IsomorphicRipple isActive={isLoading}>
        <div className={collectionGridCls} ref={this.gridRef}>
          <GridSquare columnCount="1">
            {items &&
              items.map((item: Object, idx: number) => {
                const image = extractImageFileFromData.collectionImage(item.images);
                const imageComponent = image ? (
                  <Picture
                    type="list.common"
                    artDirectionKey="collection.main"
                    image={image}
                    alt={item.name}
                    // Lazyload only images after the first one
                    lazyload={
                      idx
                        ? {
                            once: true,
                            height: LAZYLOAD_HEIGHT,
                            offset: LAZYLOAD_OFFSET,
                            placeholder: <LazyloadPlaceholder height={LAZYLOAD_HEIGHT} />,
                          }
                        : null
                    }
                  />
                ) : null;
                const authorUrl =
                  item.author &&
                  UrlAssembler.authorDetail(
                    item.author.type,
                    item.author.typeId,
                    item.author.typeAlias,
                  );

                return (
                  <GridSquareItem key={item.slug} columnCount="1">
                    <WithLocale>
                      {(locale) => (
                        <CardHorizontal
                          reversed={idx % 2}
                          link={{
                            to: UrlAssembler.collectionDetail(item.id, item.slug, { locale }),
                          }}
                          name={item.name}
                          description={item.summary}
                          bottomContent={
                            <Avatar images={item.author.avatars} name={item.author.fullName}>
                              <FormattedMessage
                                id="components.collections.curator_text"
                                values={{
                                  name: authorUrl ? (
                                    <Link type="strong" to={authorUrl} title={item.author.fullName}>
                                      {item.author.fullName}
                                    </Link>
                                  ) : (
                                    <strong>{item.author.fullName}</strong>
                                  ),
                                  curatorText: '',
                                }}
                              />
                            </Avatar>
                          }
                        >
                          {image && (
                            <MediaQuery minWidth={0}>
                              <MediaQuery maxWidth={raScreenSmMax}>{imageComponent}</MediaQuery>
                              <MediaQuery minWidth={raScreenSm}>
                                <CoverMedia>{imageComponent}</CoverMedia>
                              </MediaQuery>
                            </MediaQuery>
                          )}
                        </CardHorizontal>
                      )}
                    </WithLocale>
                  </GridSquareItem>
                );
              })}
          </GridSquare>
          {items && items.length > 0 && (
            <GridPaginationWrapper>
              <Pagination
                {...paginationProps}
                paginationType={paginationType}
                pageUrl={pageUrl}
                beforeChange={this.scrollToTopOfGrid}
              >
                {(props) => <SliderPagination {...props} />}
              </Pagination>
            </GridPaginationWrapper>
          )}
        </div>
      </IsomorphicRipple>
    );
  }
}
